import React, { useEffect, useState } from 'react';
import { AxiosError } from 'axios';
import {
  Box,
  Flex,
  Grid,
  Text,
  Spinner,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  TableContainer,
} from '@chakra-ui/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Files, Sparkle, ArrowsClockwise, ArrowSquareOut } from 'phosphor-react';
import { useAuditoriaInteligente, IPeriodoParams } from '../../../../hooks/useAuditoriaInteligente';
import { fmtDataHora, useCooldownAtivo } from '../../../../hooks/useCooldownAtivo';
import { formatCurrency } from '../../../../utils/FormatFunctions';
import { num, pct } from '../format';
import { IAuditoriaEmpresa, IDossie, IDossieAnaliseIA, AuditoriaArea } from '../types';
import { StatusBadge } from '../components/StatusBadge';
import { EmptyState } from '../components/EmptyState';
import { DossieLaudo } from './DossieLaudo';
import { DossieHero3D } from './DossieHero3D';
import { IsoOffice } from './iso/IsoOffice';
import { peopleFromAggregates } from './iso/officePeople';
import { DossieClienteTab } from './dossie/DossieClienteTab';
import { UI } from '../ui';

interface DossieScreenProps {
  empresa: IAuditoriaEmpresa | null;
  periodo: IPeriodoParams;
  /** Cruzamentos: navegar a outro submódulo da empresa. */
  onScreen: (screen: string) => void;
  /** Drill: abrir o Painel de Auditorias filtrado por área. */
  onOpenAuditoria: (area: AuditoriaArea, focusKey?: string) => void;
}

/** Cabeçalho de seção com link opcional para outro submódulo (cruzamento). */
function SectionTitle({ children, onOpen }: { children: React.ReactNode; onOpen?: () => void }) {
  return (
    <Flex justify="space-between" align="center" px={5} pt={4} pb={2}>
      <Text fontSize="13px" fontWeight={700} color={UI.ink}>
        {children}
      </Text>
      {onOpen && (
        <Button
          size="xs"
          variant="ghost"
          color={UI.brand}
          rightIcon={<ArrowSquareOut size={12} weight="bold" />}
          onClick={onOpen}
        >
          abrir
        </Button>
      )}
    </Flex>
  );
}

/** 'YYYY-MM-DD' → 'MM/AAAA'. */
const mesAno = (d?: string) => {
  const m = /^(\d{4})-(\d{2})/.exec(d ?? '');
  return m ? `${m[2]}/${m[1]}` : (d ?? '');
};

type Aba = 'cliente' | 'interno';

/** "carga_tributaria_pct" → "Carga tributaria pct". */
const humanize = (k: string) => k.replace(/_/g, ' ').replace(/^./, c => c.toUpperCase());

/** Heurística simples: valores grandes viram moeda; pequenos, número/percentual. */
const fmtValor = (k: string, v: unknown): string => {
  if (v === null || v === undefined) return '—';
  // Backend pode enviar sub-objetos (ex.: resumo de risco {score, classificacao, ...}).
  if (typeof v === 'object') {
    const o = v as Record<string, unknown>;
    if (typeof o.classificacao === 'string') return o.classificacao;
    if (o.score !== undefined && o.score !== null) return fmtValor('score', o.score);
    return '—';
  }
  const n = Number(v);
  if (Number.isNaN(n)) return typeof v === 'string' ? v : '—';
  if (k.includes('pct') || k.includes('margem') || k.includes('carga')) return `${n.toFixed(1)}%`;
  if (Math.abs(n) >= 1000) return formatCurrency(n);
  return n.toLocaleString('pt-BR');
};

/** Cor do risco a partir do status (green/amber/red). */
const riscoColor = (status?: string): string => {
  if (status === 'red') return UI.neg;
  if (status === 'amber') return UI.warn;
  return UI.pos;
};

// Cooldown (s) aplicado após cada clique — alinhado ao rate-limit de 15s do backend.
const ANALISE_COOLDOWN_S = 15;

export function DossieScreen({ empresa, periodo, onScreen, onOpenAuditoria }: DossieScreenProps) {
  const { getDossie, getDossieAnaliseIASalva, postDossieAnaliseIA } = useAuditoriaInteligente();
  const [aba, setAba] = useState<Aba>('cliente');
  const [modo, setModo] = useState<'interativo' | 'laudo'>('interativo');
  const [cooldown, setCooldown] = useState(0);
  const qc = useQueryClient();

  const { data, isFetching } = useQuery<IDossie>({
    queryKey: ['ai-dossie', { id: empresa?.id, ...periodo }],
    queryFn: getDossie,
    enabled: !!empresa,
  });

  // Análise IA salva no servidor (por empresa+período) — leitura pura, não gera.
  const analiseSalvaKey = ['ai-dossie-analise', { id: empresa?.id, ...periodo }] as const;
  const { data: analiseSalva } = useQuery<IDossieAnaliseIA | null>({
    queryKey: analiseSalvaKey,
    queryFn: getDossieAnaliseIASalva,
    enabled: !!empresa,
  });

  const analise = useMutation<
    IDossieAnaliseIA,
    AxiosError<{ detail?: string }>,
    { id: number; periodo: IPeriodoParams }
  >({
    mutationFn: vars => postDossieAnaliseIA(vars.id, vars.periodo),
    // Persiste a análise recém-gerada no cache da query. A chave vem das
    // VARIABLES da mutation (empresa/período do momento do clique), não do
    // render atual — trocar de empresa com a geração em voo não pode gravar a
    // análise da empresa antiga na chave da nova. cancelQueries antes evita
    // que um GET em voo (disparado antes do save) sobrescreva com null.
    onSuccess: async (nova, vars) => {
      const key = ['ai-dossie-analise', { id: vars.id, ...vars.periodo }] as const;
      await qc.cancelQueries({ queryKey: key });
      qc.setQueryData(key, nova);
    },
  });

  // Decrementa o cooldown a cada segundo.
  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const t = setTimeout(() => setCooldown(s => s - 1), 1000);
    return () => clearTimeout(t);
  }, [cooldown]);

  // O que exibir: a salva no servidor (o onSuccess acima mantém o cache fresco).
  const analiseAtual = analiseSalva ?? undefined;
  // Trava de 5h por empresa+período (chamada paga — o gate real é o servidor;
  // o hook re-renderiza sozinho quando o horário de liberação passa).
  const analiseBloqueada = useCooldownAtivo(analiseAtual?.atualizacao_liberada_em);

  const onGerarAnalise = () => {
    if (cooldown > 0 || analise.isPending || analiseBloqueada || !empresa) return;
    setCooldown(ANALISE_COOLDOWN_S);
    analise.mutate({ id: empresa.id, periodo });
  };

  // Mensagem de erro conforme o status: 429 (rate-limit/trava 5h, mensagem do
  // servidor em `detail`), 503 (IA indisponível) ou genérico.
  const errStatus = analise.error?.response?.status;
  let erroMsg = 'Não foi possível gerar a análise agora. Tente novamente.';
  if (errStatus === 429) {
    erroMsg =
      analise.error?.response?.data?.detail ??
      'Limite de geração atingido. Aguarde alguns segundos — ou, se o teto diário (50/dia) foi atingido, tente novamente mais tarde.';
  } else if (errStatus === 503) {
    erroMsg = 'A análise por IA está indisponível no momento. Tente novamente em instantes.';
  }

  let btnLabel = 'Gerar análise';
  if (analiseBloqueada) {
    btnLabel = `Disponível às ${fmtDataHora(analiseAtual?.atualizacao_liberada_em)}`;
  } else if (cooldown > 0) {
    btnLabel = `Aguarde ${cooldown}s`;
  } else if (analiseAtual) {
    btnLabel = 'Gerar novamente';
  }

  if (!empresa) {
    return (
      <EmptyState
        title="Selecione uma empresa"
        message="Use a busca no topo (ID, CNPJ ou Razão Social) para gerar o dossiê 360."
        icon={Files}
      />
    );
  }

  if (isFetching && !data) {
    return (
      <Box textAlign="center" py={16}>
        <Spinner />
      </Box>
    );
  }

  const emp = data?.empresa ?? {};
  const cliente = data?.cliente;
  const interno = data?.interno;

  // O backend pode enviar `score` como número ou como objeto {score, classificacao, ...}.
  const scoreRaw = interno?.score as number | Record<string, unknown> | undefined;
  const scoreNum =
    scoreRaw !== null && typeof scoreRaw === 'object' ? (scoreRaw.score as number) : scoreRaw;
  const scoreClassif =
    scoreRaw !== null && typeof scoreRaw === 'object'
      ? (scoreRaw.classificacao as string | undefined)
      : undefined;

  // Saúde do cliente (string ou objeto)
  const saude = cliente?.saude;
  const saudeTexto =
    typeof saude === 'string' ? saude : (saude?.texto ?? saude?.label ?? undefined);

  const tabs: { value: Aba; label: string }[] = [
    { value: 'cliente', label: 'Dossiê do Cliente' },
    { value: 'interno', label: 'Dossiê Interno · Contabilidade' },
  ];

  const periodoLabel = `${mesAno(periodo.data_ini)} – ${mesAno(periodo.data_fim)}`;
  const responsavel = (emp.relacionamento_com_cliente ?? undefined) as string | undefined;

  // Props do hero 3D
  const nomeEmp = emp.razao_social || emp.nome || empresa.razao_social || empresa.nome || 'Empresa';
  const idLinhaHero = [
    (emp.cnpj || empresa.cnpj) && `CNPJ ${emp.cnpj || empresa.cnpj}`,
    (emp.regime || empresa.regime) && `Regime ${emp.regime || empresa.regime}`,
    (emp.uf || empresa.uf) && `${emp.uf || empresa.uf}`,
  ]
    .filter(Boolean)
    .join('  ·  ');
  const porGeneroHero = cliente?.equipe?.por_genero ?? [];
  const colaboradoresHero = porGeneroHero.reduce((s, g) => s + num(g.qtd), 0);
  const resumoEnt = Object.entries(cliente?.resumo ?? {}).filter(
    ([, v]) => v !== null && v !== undefined,
  );
  const statsPick = resumoEnt.filter(([k]) =>
    /fatur|receita|imposto|folha|result|margem|carga/.test(k.toLowerCase()),
  );
  const statsHero = (statsPick.length ? statsPick : resumoEnt)
    .slice(0, 3)
    .map(([k, v]) => ({ label: humanize(k), value: fmtValor(k, v) }));

  // Alterna entre a visão interativa (drill/IA) e o laudo (documento p/ PDF).
  const Toggle = (
    <Flex
      className="laudo-noprint"
      gap={1}
      p={1}
      mb={5}
      bg={UI.rowHover}
      borderRadius="11px"
      w="fit-content"
    >
      {(['interativo', 'laudo'] as const).map(m => (
        <Box
          key={m}
          as="button"
          px={4}
          py={1.5}
          borderRadius="9px"
          fontSize="12.5px"
          fontWeight={700}
          bg={modo === m ? 'white' : 'transparent'}
          color={modo === m ? UI.brand : UI.muted}
          boxShadow={modo === m ? '0 1px 4px rgba(15,23,42,0.12)' : 'none'}
          onClick={() => setModo(m)}
        >
          {m === 'interativo' ? 'Interativo' : 'Laudo (PDF)'}
        </Box>
      ))}
    </Flex>
  );

  if (modo === 'laudo') {
    return (
      <Box>
        {Toggle}
        <DossieLaudo
          emp={emp}
          cliente={cliente}
          interno={interno}
          scoreNum={scoreNum}
          scoreClassif={scoreClassif}
          periodoLabel={periodoLabel}
          responsavel={responsavel}
        />
      </Box>
    );
  }

  return (
    <Box>
      {Toggle}

      <DossieHero3D
        nome={nomeEmp}
        idLinha={idLinhaHero}
        periodoLabel={periodoLabel}
        score={scoreNum}
        classif={scoreClassif}
        colaboradores={colaboradoresHero}
        porGenero={porGeneroHero}
        stats={statsHero}
      />

      {/* Análise inteligente (IA) */}
      <Box
        bgGradient="linear(120deg, #F3F0FE, #EDE9FE)"
        border="1px solid #D6D0F5"
        borderRadius="14px"
        p={5}
        mb={5}
      >
        <Flex justify="space-between" align="center" gap={4} wrap="wrap">
          <Flex align="center" gap={3}>
            <Flex
              align="center"
              justify="center"
              w="40px"
              h="40px"
              borderRadius="10px"
              bg="white"
              color="#7C5CE0"
              flexShrink={0}
            >
              <Sparkle size={20} weight="fill" />
            </Flex>
            <Box>
              <Text fontWeight={700} color={UI.ink}>
                Análise inteligente do período
              </Text>
              <Text fontSize="12.5px" color={UI.muted}>
                Resumo escrito por IA sobre os números deste período.
              </Text>
            </Box>
          </Flex>
          <Box textAlign="end">
            <Button
              leftIcon={analise.isPending ? <ArrowsClockwise size={15} /> : <Sparkle size={15} />}
              bg="#7C5CE0"
              color="white"
              _hover={{ bg: '#6B4FD0' }}
              size="sm"
              isLoading={analise.isPending}
              isDisabled={cooldown > 0 || analise.isPending || analiseBloqueada}
              loadingText="Gerando…"
              title={
                analiseBloqueada
                  ? `Análise gerada há menos de 5h — nova geração às ${fmtDataHora(
                      analiseAtual?.atualizacao_liberada_em,
                    )}`
                  : undefined
              }
              onClick={onGerarAnalise}
            >
              {btnLabel}
            </Button>
            {analiseAtual?.gerado_em && (
              <Text fontSize="11px" color={UI.muted} mt={1} whiteSpace="nowrap">
                Atualizado em {fmtDataHora(analiseAtual.gerado_em)}
              </Text>
            )}
          </Box>
        </Flex>

        {analise.isError && (
          <Box bg={UI.negSoft} color={UI.neg} borderRadius="10px" p={3} mt={4} fontSize="13px">
            {erroMsg}
          </Box>
        )}
        {analiseAtual && (
          <Box bg="#FBFAFF" border="1px solid #E6E1F7" borderRadius="12px" p={4} mt={4}>
            {(analiseAtual.texto ?? '')
              .split('\n')
              .filter(Boolean)
              .map(p => (
                <Text key={p} fontSize="13.5px" color={UI.muted} mb={2} lineHeight="1.6">
                  {p}
                </Text>
              ))}
            {analiseAtual.modelo && (
              <Text fontSize="11px" color={UI.muted} mt={2}>
                Gerado por {analiseAtual.modelo}
                {analiseAtual.gerado_em ? ` em ${fmtDataHora(analiseAtual.gerado_em)}` : ''}
              </Text>
            )}
          </Box>
        )}
      </Box>

      {/* Abas Cliente / Interno */}
      <Flex gap={1} borderBottom="1px solid #E7ECF2" mb={5} wrap="wrap">
        {tabs.map(t => {
          const isActive = t.value === aba;
          return (
            <Flex
              key={t.value}
              px={4}
              py={3}
              cursor="pointer"
              onClick={() => setAba(t.value)}
              borderBottom="2.5px solid"
              borderColor={isActive ? UI.brand : 'transparent'}
              color={isActive ? UI.brand : UI.muted}
              mb="-1px"
              _hover={{ color: isActive ? UI.brand : UI.ink }}
            >
              <Text fontSize="13px" fontWeight={700}>
                {t.label}
              </Text>
            </Flex>
          );
        })}
      </Flex>

      {aba === 'cliente' && (
        <Box>
          {(cliente?.equipe?.por_cargo ?? []).length > 0 && (
            <Box mb={5}>
              <IsoOffice
                people={peopleFromAggregates(
                  cliente?.equipe?.por_cargo ?? [],
                  cliente?.equipe?.por_genero ?? [],
                  empresa?.id ?? 1,
                )}
                seed={empresa?.id ?? 1}
              />
            </Box>
          )}

          <DossieClienteTab
            cliente={cliente}
            scoreNum={scoreNum}
            scoreClassif={scoreClassif}
            saudeTexto={saudeTexto}
            riscos={interno?.mapa_riscos ?? []}
            colaboradores={colaboradoresHero}
            porGenero={porGeneroHero}
            onScreen={onScreen}
          />
        </Box>
      )}

      {aba === 'interno' && (
        <Box>
          {(interno?.mapa_riscos ?? []).length > 0 && (
            <Grid templateColumns={{ base: '1fr', md: '1fr 1fr' }} gap={3} mb={5}>
              {(interno?.mapa_riscos ?? []).map(r => {
                const color = riscoColor(r.status as string | undefined);
                const drill = r.area ? () => onOpenAuditoria(r.area as AuditoriaArea) : undefined;
                return (
                  <Flex
                    key={`${r.label ?? r.area ?? 'risco'}-${fmtValor('valor', r.valor)}`}
                    align="center"
                    gap={3}
                    bg="white"
                    border="1px solid"
                    borderColor={UI.border}
                    borderRadius="11px"
                    p="12px 14px"
                    cursor={drill ? 'pointer' : undefined}
                    onClick={drill}
                    _hover={
                      drill
                        ? { borderColor: color, boxShadow: `0 4px 12px -6px ${color}80` }
                        : undefined
                    }
                    title={drill ? 'Abrir no Painel de Auditorias' : undefined}
                  >
                    <Box w="10px" h="10px" borderRadius="50%" bg={color} flexShrink={0} />
                    <Text flex={1} fontSize="12.5px" fontWeight={600} color={UI.muted}>
                      {r.label ?? r.area}
                    </Text>
                    <Text fontSize="13px" fontWeight={800} color={color} whiteSpace="nowrap">
                      {fmtValor('valor', r.valor)}
                    </Text>
                    {drill && (
                      <Box color={UI.faint} flexShrink={0}>
                        <ArrowSquareOut size={12} weight="bold" />
                      </Box>
                    )}
                  </Flex>
                );
              })}
            </Grid>
          )}

          {(interno?.apontamentos ?? []).length > 0 && (
            <Box
              bg="white"
              border="1px solid"
              borderColor={UI.border}
              borderRadius="16px"
              overflow="hidden"
              mb={5}
            >
              <SectionTitle onOpen={() => onScreen('auditorias')}>
                Principais apontamentos
              </SectionTitle>
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead bg={UI.rowHover}>
                    <Tr>
                      <Th>Regra</Th>
                      <Th>Apontamento</Th>
                      <Th>Período</Th>
                      <Th>Status</Th>
                      <Th isNumeric>Diferença</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(interno?.apontamentos ?? []).map(a => (
                      <Tr key={`${a.regra}-${a.periodo}`} _hover={{ bg: UI.rowHover }}>
                        <Td fontWeight={700}>{a.regra}</Td>
                        <Td maxW="320px" whiteSpace="normal">
                          {a.descricao ?? a.titulo}
                        </Td>
                        <Td>{a.periodo}</Td>
                        <Td>
                          {a.badge || a.status ? (
                            <StatusBadge
                              badge={a.badge}
                              status={
                                a.status === 'DIVERGENCIA' ||
                                a.status === 'ALERTA' ||
                                a.status === 'OK'
                                  ? a.status
                                  : undefined
                              }
                            />
                          ) : (
                            '—'
                          )}
                        </Td>
                        <Td isNumeric fontWeight={700} color={num(a.diferenca) ? UI.neg : UI.muted}>
                          {num(a.diferenca) ? formatCurrency(num(a.diferenca)) : '—'}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {(interno?.societario ?? []).length > 0 && (
            <Box
              bg="white"
              border="1px solid"
              borderColor={UI.border}
              borderRadius="16px"
              overflow="hidden"
            >
              <SectionTitle onOpen={() => onScreen('societario')}>Quadro societário</SectionTitle>
              <TableContainer>
                <Table variant="simple" size="sm">
                  <Thead bg={UI.rowHover}>
                    <Tr>
                      <Th>Sócio</Th>
                      <Th>CPF/CNPJ</Th>
                      <Th>Cargo</Th>
                      <Th isNumeric>Participação</Th>
                    </Tr>
                  </Thead>
                  <Tbody>
                    {(interno?.societario ?? []).map(s => (
                      <Tr key={`${s.cpf_cnpj}-${s.nome}`} _hover={{ bg: UI.rowHover }}>
                        <Td fontWeight={700}>{s.nome}</Td>
                        <Td>{s.cpf_cnpj}</Td>
                        <Td>{s.cargo}</Td>
                        <Td isNumeric fontWeight={700}>
                          {pct(s.participacao)}
                        </Td>
                      </Tr>
                    ))}
                  </Tbody>
                </Table>
              </TableContainer>
            </Box>
          )}

          {!interno && (
            <Text fontSize="13px" color={UI.muted}>
              Sem dados internos no dossiê.
            </Text>
          )}
        </Box>
      )}
    </Box>
  );
}
